import { useCallback, useEffect } from 'react';

export const useChartRegistration = (
  chartRef,
  chartContainerRef,
  containerId,
  syncGroupId,
  setSyncGroupId
) => {
  // Функция регистрации графика в менеджере синхронизации
  const registerChart = useCallback(() => {
    if (!chartRef.current) {
      console.log(`BaseChart: Невозможно зарегистрировать график - chartRef не инициализирован`);
      return false;
    }

    // Проверяем что это действительно инстанс Chart.js
    if (!chartRef.current.canvas || !chartRef.current.scales || !chartRef.current.update) {
      console.log(`BaseChart: График ${containerId} еще не полностью инициализирован - отсутствуют необходимые свойства`);
      return false;
    }

    const canvas = chartRef.current.canvas;
    if (!canvas) {
      console.log(`BaseChart: Невозможно зарегистрировать график - canvas не найден`);
      return false;
    }

    console.log(`BaseChart: Регистрация графика ${containerId} в менеджере синхронизации`);
    
    // Устанавливаем атрибуты для идентификации графика
    canvas.setAttribute('data-graph', 'true');
    canvas.setAttribute('data-container-id', containerId);
    
    // Проверяем, находится ли график в разделенном контейнере
    const isInSplitContainer = containerId.includes('-');
    
    if (isInSplitContainer) {
      const baseContainerId = containerId.split('-')[0];
      canvas.setAttribute('data-parent-container', baseContainerId);
      
      // Получаем группу синхронизации из контейнера или создаем новую
      const container = chartContainerRef.current?.closest('.split-screen-container');
      const containerSyncGroupId = container?.getAttribute('data-sync-group') || `sync-${containerId}`;
      
      // Устанавливаем syncGroupId
      setSyncGroupId(containerSyncGroupId);
      
      // Добавляем информацию о группе синхронизации
      canvas.setAttribute('data-sync-group', containerSyncGroupId);
      
      console.log(`BaseChart: График в разделенном контейнере ${containerId} инициализирован, родитель: ${baseContainerId}, группа: ${containerSyncGroupId}`);
      
      // Отправляем событие о готовности графика в разделенном контейнере
      document.dispatchEvent(new CustomEvent('splitChartInitialized', {
        detail: {
          containerId,
          baseContainerId,
          syncGroupId: containerSyncGroupId,
          timestamp: Date.now()
        }
      }));
    }
    
    // Регистрируем график в chartSyncManager
    if (window.chartSyncManager) {
      // Добавляем прямой доступ к экземпляру графика через canvas
      canvas.__chartjs__ = chartRef.current;
      canvas.__chartInstance = chartRef.current;
      
      // Регистрируем график в менеджере синхронизации
      window.chartSyncManager.registerChart(containerId, chartRef.current);
      
      console.log(`BaseChart: График ${containerId} зарегистрирован в ChartSyncManager`);
      return true;
    } else {
      console.warn(`BaseChart: chartSyncManager не найден, синхронизация не будет работать`);
      return false;
    }
  }, [containerId, chartRef, chartContainerRef, setSyncGroupId]);

  // Эффект для регистрации графика в менеджере синхронизации
  useEffect(() => {
    let registrationTimeout = null;
    let chartRegistered = false;
    let retryCount = 0;
    const maxRetries = 50; // Увеличиваем максимальное количество попыток
    
    const registerChartWithRetry = () => {
      retryCount++;
      
      // Расширенная проверка готовности графика
      const isChartReady = () => {
        if (!chartRef.current) return false;
        
        // Проверяем основные свойства Chart.js
        const hasCanvas = !!chartRef.current.canvas;
        const hasScales = !!chartRef.current.scales;
        const hasUpdate = typeof chartRef.current.update === 'function';
        const hasData = !!chartRef.current.data;
        const isNotDestroyed = !chartRef.current.isDestroyed;
        
        const ready = hasCanvas && hasScales && hasUpdate && hasData && isNotDestroyed;
        
        if (!ready) {
          console.log(`BaseChart: График ${containerId} не готов (попытка ${retryCount}/${maxRetries}):`, {
            hasCanvas,
            hasScales,
            hasUpdate,
            hasData,
            isNotDestroyed
          });
        }
        
        return ready;
      };
      
      // Проверяем, что график инициализирован и готов
      if (isChartReady()) {
        if (!chartRegistered) {
          console.log(`BaseChart: Регистрируем график ${containerId} в менеджере синхронизации (попытка ${retryCount})`);
          const success = registerChart();
          
          if (success) {
            chartRegistered = true;
            
            // Отправляем событие, что график инициализирован
            document.dispatchEvent(new CustomEvent('chartInitialized', {
              detail: {
                containerId,
                timestamp: Date.now()
              }
            }));
            
            console.log(`BaseChart: График ${containerId} успешно зарегистрирован после ${retryCount} попыток`);
          } else {
            // Если регистрация не удалась, пробуем еще раз
            if (retryCount < maxRetries) {
              registrationTimeout = setTimeout(registerChartWithRetry, 200);
            }
          }
        }
      } else {
        // Если график еще не инициализирован, пробуем еще раз через некоторое время
        if (retryCount < maxRetries) {
          console.log(`BaseChart: График ${containerId} еще не готов, пробуем еще раз через 200мс (попытка ${retryCount}/${maxRetries})`);
          registrationTimeout = setTimeout(registerChartWithRetry, 200);
        } else {
          console.warn(`BaseChart: Не удалось зарегистрировать график ${containerId} после ${maxRetries} попыток`);
        }
      }
    };
    
    // Обработчик события готовности синхронизации после разделения экрана
    const handleChartSyncReady = (event) => {
      const { totalCharts, newCharts } = event.detail;
      console.log(`BaseChart: Получено событие chartSyncReady для ${containerId}`, {
        totalCharts,
        newCharts
      });
      
      // Проверяем, зарегистрирован ли уже наш график
      if (chartRef.current && !chartRegistered) {
        console.log(`BaseChart: Повторная регистрация графика ${containerId} после разделения экрана`);
        retryCount = 0; // Сбрасываем счетчик попыток
        registerChartWithRetry();
      }
    };
    
    document.addEventListener('chartSyncReady', handleChartSyncReady);
    
    // Запускаем процесс регистрации с небольшой задержкой, чтобы дать времени компоненту инициализироваться
    const initialDelay = setTimeout(() => {
      registerChartWithRetry();
    }, 100);
    
    // Дополнительные попытки регистрации через определенные интервалы
    const additionalRetries = [500, 1000, 2000, 5000].map((timeout) => {
      return setTimeout(() => {
        if (!chartRegistered && chartRef.current && !chartRef.current.isDestroyed) {
          console.log(`BaseChart: Дополнительная попытка регистрации графика ${containerId} через ${timeout}мс`);
          retryCount = 0; // Сбрасываем счетчик для дополнительных попыток
          registerChartWithRetry();
        }
      }, timeout);
    });
    
    // Очистка при размонтировании
    return () => {
      document.removeEventListener('chartSyncReady', handleChartSyncReady);
      clearTimeout(registrationTimeout);
      clearTimeout(initialDelay);
      additionalRetries.forEach(clearTimeout);
      
      // Освобождаем ресурсы графика
      if (chartRef.current) {
        if (window.chartSyncManager) {
          console.log(`BaseChart: Удаляем график ${containerId} из менеджера синхронизации`);
          window.chartSyncManager.unregisterChart(containerId);
        }
        
        try {
          if (chartRef.current.destroy && !chartRef.current.isDestroyed) {
            chartRef.current.destroy();
          }
        } catch (e) {
          console.warn(`BaseChart: Ошибка при уничтожении графика ${containerId}:`, e);
        }
      }
    };
  }, [containerId, registerChart, chartRef]);

  return {
    registerChart
  };
}; 